import { useMemo } from "react";

import { ColocaSelect } from "@/shared/components/forms/ColocaSelect";
import useTranslations from "@/shared/lang/useTranslations";

export interface StatementMonthPickerProps {
  /** `YYYY-MM`. */
  value: string;
  onChange: (month: string) => void;
  /** ISO date the card was created — the oldest selectable month. */
  cardCreatedAt: string | undefined;
  disabled?: boolean;
}

const localeFor = (lang: string) => (lang === "es" ? "es-ES" : lang === "pt" ? "pt-BR" : "en-US");

const toMonthKey = (year: number, monthIndex: number) => `${year}-${String(monthIndex + 1).padStart(2, "0")}`;

export const StatementMonthPicker = ({
  value,
  onChange,
  cardCreatedAt,
  disabled = false,
}: StatementMonthPickerProps) => {
  const { t, activeLanguage } = useTranslations("cards");

  const options = useMemo(() => {
    const formatter = new Intl.DateTimeFormat(localeFor(activeLanguage), {
      month: "long",
      year: "numeric",
    });
    const now = new Date();
    const start = cardCreatedAt ? new Date(cardCreatedAt) : now;
    const oldest = Number.isNaN(start.getTime()) ? now : start;

    const months: { value: string; label: string }[] = [];
    const cursor = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
    const floor = Date.UTC(oldest.getUTCFullYear(), oldest.getUTCMonth(), 1);

    while (cursor.getTime() >= floor) {
      months.push({
        value: toMonthKey(cursor.getUTCFullYear(), cursor.getUTCMonth()),
        label: formatter.format(cursor),
      });
      cursor.setUTCMonth(cursor.getUTCMonth() - 1);
    }

    return months;
  }, [activeLanguage, cardCreatedAt]);

  return (
    <ColocaSelect
      value={value}
      onValueChange={onChange}
      options={options}
      disabled={disabled}
      aria-label={t("statement_month_label")}
      className="w-44"
    />
  );
};
